import { ApolloError, useQuery } from '@apollo/client'
import { Interface } from '@ethersproject/abi'
import { abi as IUniswapV3PoolImmutablesABI } from '@uniswap/v3-core/artifacts/contracts/interfaces/pool/IUniswapV3PoolImmutables.sol/IUniswapV3PoolImmutables.json'
import { FeeAmount } from '@uniswap/v3-sdk'
import { useWeb3React } from '@web3-react/core'
import gql from 'graphql-tag'
import { NEVER_RELOAD, useMultipleContractSingleData } from 'lib/hooks/multicall'
import { useEffect, useMemo, useRef } from 'react'
import { Incentive } from 'types/incentives'
import { addHexPrefix } from 'utils/addHexPrefix'

import ERC20ABI from '../../abis/erc20.json'
import { useV3StakerApolloClient } from './apollo'

// GraphQL query for counting total incentives
const INCENTIVES_COUNT_QUERY = gql`
  query IncentivesCount {
    incentives {
      rewardToken
    }
  }
`

const INCENTIVES_QUERY = gql`
  query Incentives($skip: Int!, $first: Int!) {
    incentives(orderBy: endTime, orderDirection: desc, skip: $skip, first: $first) {
      id
      startTime
      endTime
      pool
      reward
      rewardToken
      refundee
    }
  }
`

// Types for the query response
interface IncentiveProgram {
  id: string
  startTime: string
  endTime: string
  pool: string // Pool address
  reward: string
  rewardToken: string
  refundee: string
}

interface IncentivesQueryResult {
  data: Incentive[] | undefined
  loading: boolean
  error: ApolloError | undefined
  pageCount: number
}

// Pool metadata structure
interface PoolMetadata {
  address: string
  token0: string
  token1: string
  fee: FeeAmount
  token0Symbol?: string
  token1Symbol?: string
}

// Interface for pool contract
const POOL_IMMUTABLES_INTERFACE = new Interface(IUniswapV3PoolImmutablesABI)
const ERC20_INTERFACE = new Interface(ERC20ABI)

/**
 * Hook to fetch incentives data from the subgraph with pagination
 * @param pageSize Number of incentives to fetch per page (default: 10)
 * @param pageNumber Current page number (starting from 1) (default: 1)
 * @param interval Poll interval in milliseconds (optional)
 * @returns Query result object with data, loading state, error, and page count
 */
export function useIncentivesQuery(pageSize = 10, pageNumber = 1, interval?: number): IncentivesQueryResult {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)
  const { chainId } = useWeb3React()
  const client = useV3StakerApolloClient()

  // Calculate skip based on page number and size
  const skip = (pageNumber - 1) * pageSize

  // Fetch the paginated incentives
  const {
    data,
    loading: incentivesLoading,
    error,
    refetch: refetchIncentives,
  } = useQuery(INCENTIVES_QUERY, {
    variables: { skip, first: pageSize },
    pollInterval: interval,
    client,
  })

  // Fetch the total count of incentives for pagination
  const {
    data: countData,
    loading: countLoading,
    refetch: refetchCount,
  } = useQuery(INCENTIVES_COUNT_QUERY, {
    client,
  })

  // Calculate total page count
  const pageCount = useMemo(() => {
    if (!countData?.incentives) return 0
    return Math.ceil(countData.incentives.length / pageSize)
  }, [countData, pageSize])

  useEffect(() => {
    // Refetch after interval so chainId can be updated in the query middleware
    timeoutIdRef.current = setTimeout(() => {
      refetchIncentives({ skip, first: pageSize })
      refetchCount()
    }, 1000)

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
        timeoutIdRef.current = null
      }
    }
  }, [refetchIncentives, refetchCount, chainId, skip, pageSize])

  // Extract pool addresses from incentives data
  const poolAddresses = useMemo<string[]>(() => {
    if (!data?.incentives || !data.incentives.length) return []
    return data.incentives.map((incentive: IncentiveProgram) => incentive.pool)
  }, [data])

  // Fetch token0, token1, and fee from each pool contract
  const token0Results = useMultipleContractSingleData(
    poolAddresses,
    POOL_IMMUTABLES_INTERFACE,
    'token0',
    undefined,
    NEVER_RELOAD
  )

  const token1Results = useMultipleContractSingleData(
    poolAddresses,
    POOL_IMMUTABLES_INTERFACE,
    'token1',
    undefined,
    NEVER_RELOAD
  )
  const feeResults = useMultipleContractSingleData(
    poolAddresses,
    POOL_IMMUTABLES_INTERFACE,
    'fee',
    undefined,
    NEVER_RELOAD
  )

  // Combine the results to create pool metadata
  const poolMetadata = useMemo(() => {
    if (!poolAddresses.length || !chainId) return undefined

    return poolAddresses
      .map((address, i) => {
        const token0Result = token0Results[i]
        const token1Result = token1Results[i]
        const feeResult = feeResults[i]

        // Skip if any data is still loading or invalid
        if (
          token0Result?.loading ||
          token1Result?.loading ||
          feeResult?.loading ||
          !token0Result?.result ||
          !token1Result?.result ||
          !feeResult?.result
        ) {
          return undefined
        }

        return {
          address,
          token0: token0Result.result[0],
          token1: token1Result.result[0],
          fee: feeResult.result[0] as FeeAmount,
        }
      })
      .filter((pool): pool is PoolMetadata => !!pool)
  }, [poolAddresses, token0Results, token1Results, feeResults, chainId])

  // Get all unique token addresses from pools
  const tokenAddresses = useMemo(() => {
    if (!poolMetadata) return []

    const addresses = new Set<string>()

    poolMetadata.forEach((pool) => {
      addresses.add(pool.token0)
      addresses.add(pool.token1)
    })

    // Also add reward tokens from incentives
    if (data?.incentives) {
      data.incentives.forEach((incentive: IncentiveProgram) => {
        addresses.add(incentive.rewardToken)
      })
    }

    return Array.from(addresses)
  }, [poolMetadata, data])

  // Fetch symbols for all tokens
  const symbolResults = useMultipleContractSingleData(
    tokenAddresses,
    ERC20_INTERFACE,
    'symbol',
    undefined,
    NEVER_RELOAD
  )

  // Create a map of token address to symbol
  const tokenSymbolMap = useMemo(() => {
    const map: { [address: string]: string } = {}

    tokenAddresses.forEach((address, i) => {
      const result = symbolResults[i]
      if (!result?.loading && result?.result) {
        map[address.toLowerCase()] = result.result[0]
      }
    })

    return map
  }, [tokenAddresses, symbolResults])

  // Update pool metadata with token symbols
  const enhancedPoolMetadata = useMemo(() => {
    if (!poolMetadata || !tokenSymbolMap) return undefined

    return poolMetadata.map((pool) => ({
      ...pool,
      token0Symbol: tokenSymbolMap[pool.token0.toLowerCase()],
      token1Symbol: tokenSymbolMap[pool.token1.toLowerCase()],
    }))
  }, [poolMetadata, tokenSymbolMap])

  // Format incentives to match the required structure
  const incentives = useMemo<Incentive[]>(() => {
    if (!data?.incentives || !data.incentives.length || !enhancedPoolMetadata) return []

    const result: Incentive[] = data.incentives
      .map((incentive: IncentiveProgram) => {
        // Find pool metadata for this incentive
        const poolInfo = enhancedPoolMetadata.find(
          (pool) => pool.address.toLowerCase() === incentive.pool.toLowerCase()
        )

        if (!poolInfo || !poolInfo.token0Symbol || !poolInfo.token1Symbol) {
          return undefined
        }

        // Create the name in format "TokenA:TokenB" or with reward token if available
        const name = `${poolInfo.token0Symbol}:${poolInfo.token1Symbol}`

        // Convert startTime and endTime from string to number
        const startTime = parseInt(incentive.startTime)
        const endTime = parseInt(incentive.endTime)

        return {
          name,
          tokenIdA: poolInfo.token0,
          tokenIdB: poolInfo.token1,
          fee: Number(poolInfo.fee),
          key: {
            rewardToken: incentive.rewardToken,
            pool: incentive.pool,
            startTime,
            endTime,
            refundee: incentive.refundee,
          },
          reward: incentive.reward,
        }
      })
      .filter((incentive: Incentive | undefined) => incentive !== undefined) as Incentive[]

    return result
  }, [data, enhancedPoolMetadata])

  const loading = incentivesLoading || countLoading || (!!poolMetadata && symbolResults.some((r) => r.loading))

  return useMemo(
    () => ({
      data: incentives.length > 0 ? incentives : undefined,
      loading,
      error,
      pageCount,
    }),
    [loading, error, incentives, pageCount]
  )
}

// eslint-disable-next-line import/no-unused-modules
export function useIncentivesRewardTokensQuery(): {
  /**
   * Token addreses.
   */
  data: string[]
  loading: boolean
} {
  const client = useV3StakerApolloClient()
  const { data, loading } = useQuery(INCENTIVES_COUNT_QUERY, {
    client,
  })

  const rewardTokens = useMemo<string[]>(() => {
    if (!data?.incentives) return []

    const addresses = data.incentives.map((incentive: IncentiveProgram) => incentive.rewardToken)
    return Array.from(new Set(addresses))
  }, [data?.incentives])

  return useMemo(() => ({ data: rewardTokens, loading }), [rewardTokens, loading])
}

const INCENTIVE_BY_ID_QUERY = gql`
  query IncentiveById($id: String!) {
    incentives(where: { id: $id }) {
      id
      startTime
      endTime
      pool
      reward
      rewardToken
      refundee
    }
  }
`
/**
 * Hook to fetch a specific incentive by its ID
 * @param id The incentive ID to query for
 * @returns An object containing the incentive data, loading state and error
 */
// eslint-disable-next-line import/no-unused-modules
export function useIncentiveQuery(id: string | null): {
  data: Incentive | undefined
  loading: boolean
  error: ApolloError | undefined
} {
  const client = useV3StakerApolloClient()
  const { chainId } = useWeb3React()

  const {
    data,
    loading: incentiveLoading,
    error,
  } = useQuery(INCENTIVE_BY_ID_QUERY, {
    variables: { id: id ? addHexPrefix(id) : id },
    skip: !id || !chainId,
    client,
  })

  // Extract pool address from incentive data
  const poolAddress = useMemo<string | undefined>(() => {
    if (!data?.incentives || !data.incentives.length) return undefined
    return data.incentives[0].pool
  }, [data])

  // Fetch token0, token1, and fee from the pool contract
  const token0Results = useMultipleContractSingleData(
    poolAddress ? [poolAddress] : [],
    POOL_IMMUTABLES_INTERFACE,
    'token0',
    undefined,
    NEVER_RELOAD
  )

  const token1Results = useMultipleContractSingleData(
    poolAddress ? [poolAddress] : [],
    POOL_IMMUTABLES_INTERFACE,
    'token1',
    undefined,
    NEVER_RELOAD
  )

  const feeResults = useMultipleContractSingleData(
    poolAddress ? [poolAddress] : [],
    POOL_IMMUTABLES_INTERFACE,
    'fee',
    undefined,
    NEVER_RELOAD
  )

  // Combine the results to create pool metadata
  const poolMetadata = useMemo(() => {
    if (!poolAddress || !chainId) return undefined

    const token0Result = token0Results[0]
    const token1Result = token1Results[0]
    const feeResult = feeResults[0]

    if (
      token0Result?.loading ||
      token1Result?.loading ||
      feeResult?.loading ||
      !token0Result?.result ||
      !token1Result?.result ||
      !feeResult?.result
    ) {
      return undefined
    }

    return {
      address: poolAddress,
      token0: token0Result.result[0],
      token1: token1Result.result[0],
      fee: feeResult.result[0] as FeeAmount,
    }
  }, [poolAddress, token0Results, token1Results, feeResults, chainId])

  // Get the incentive data for reward token
  const rewardToken = useMemo<string | undefined>(() => {
    if (!data?.incentives || !data.incentives.length) return undefined
    return data.incentives[0].rewardToken
  }, [data])

  // Get all token addresses from the pool
  const tokenAddresses = useMemo(() => {
    if (!poolMetadata) return []

    const addresses = new Set<string>()
    addresses.add(poolMetadata.token0)
    addresses.add(poolMetadata.token1)

    // Also add reward token
    if (rewardToken) {
      addresses.add(rewardToken)
    }

    return Array.from(addresses)
  }, [poolMetadata, rewardToken])

  // Fetch symbols for all tokens
  const symbolResults = useMultipleContractSingleData(
    tokenAddresses,
    ERC20_INTERFACE,
    'symbol',
    undefined,
    NEVER_RELOAD
  )

  // Create a map of token address to symbol
  const tokenSymbolMap = useMemo(() => {
    const map: { [address: string]: string } = {}

    tokenAddresses.forEach((address, i) => {
      const result = symbolResults[i]
      if (!result?.loading && result?.result) {
        map[address.toLowerCase()] = result.result[0]
      }
    })

    return map
  }, [tokenAddresses, symbolResults])

  // Format the incentive to match the required structure
  const incentive = useMemo<Incentive | undefined>(() => {
    if (!data?.incentives || !data.incentives.length || !poolMetadata) return undefined

    const incentiveData = data.incentives[0]
    const token0Symbol = tokenSymbolMap[poolMetadata.token0.toLowerCase()]
    const token1Symbol = tokenSymbolMap[poolMetadata.token1.toLowerCase()]

    if (!token0Symbol || !token1Symbol) return undefined

    // Create the name in format "TokenA:TokenB"
    const name = `${token0Symbol}:${token1Symbol}`

    // Convert startTime and endTime from string to number
    const startTime = parseInt(incentiveData.startTime)
    const endTime = parseInt(incentiveData.endTime)

    return {
      name,
      tokenIdA: poolMetadata.token0,
      tokenIdB: poolMetadata.token1,
      fee: Number(poolMetadata.fee),
      key: {
        rewardToken: incentiveData.rewardToken,
        pool: incentiveData.pool,
        startTime,
        endTime,
        refundee: incentiveData.refundee,
      },
      reward: incentiveData.reward,
    }
  }, [data, poolMetadata, tokenSymbolMap])

  const loading =
    incentiveLoading ||
    (poolAddress &&
      (token0Results.some((r) => r.loading) ||
        token1Results.some((r) => r.loading) ||
        feeResults.some((r) => r.loading))) ||
    symbolResults.some((r) => r.loading)

  return {
    data: incentive,
    loading,
    error,
  }
}
